import React, { useEffect, useState } from "react";
import { Card, Table, Typography, Tag, Empty, Spin, Space } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import tenderDashboardApis from "../../api/tenderDashboard/tenderDashboardApis";

const { Title, Text } = Typography;

type Awardee = {
  id: string;
  name: string;
  type: "Supplier" | "Contractor" | string;
  value: number;
};

const TopAwardeesTable: React.FC = () => {
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgId = currentUser?.orgid;

  const [data, setData] = useState<Awardee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await tenderDashboardApis.getTopAwardees({
        orgIds: String(orgId),
      });
      const rows: any[] = res?.data?.data || [];
      const mapped: Awardee[] = rows.map((r) => ({
        id: String(r?.respondentId ?? ""),
        name: r?.respondentName ?? "",
        type: r?.respondentType ?? "",
        value: r?.totalAwardedValue ?? 0,
      }));
      setData(mapped);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const handleRefresh = () => {
    fetchData();
  };

  const formatCurrency = (value: number) => {
    return `₹ ${(value ?? 0).toLocaleString("en-IN")}`;
  };

  const columns: ColumnsType<Awardee> = [
    {
      title: "Party Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <a onClick={() => console.log(`Go to profile of ${record.id}`)}>
          {text}
        </a>
      ),
    },
    {
      title: "Party Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Tag color={type === "Supplier" ? "blue" : "green"}>{type}</Tag>
      ),
    },
    {
      title: "Value Awarded",
      dataIndex: "value",
      key: "value",
      align: "right",
      render: (value) => <strong>{formatCurrency(value)}</strong>,
    },
  ];

  return (
    <Card
      title={
        <div style={{ padding: "10px 0" }}>
          <Title level={5} style={{ marginBottom: 0, color: "#004293" }}>
            Top 5 Awardees
          </Title>
          <Text type="secondary" style={{ fontSize: 12, color: "#8094ae" }}>
            Snapshot of suppliers and contractors with highest awards
          </Text>
        </div>
      }
      extra={
        <Space>
          <ReloadOutlined
            onClick={handleRefresh}
            style={{ cursor: "pointer" }}
          />
        </Space>
      }
      style={{ width: "100%" }}
      bodyStyle={{ padding: 0 }}
    >
      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center" }}>
          <Spin tip="Loading table..." />
        </div>
      ) : !data || data.length === 0 ? (
        <Empty
          description="No awardee data found"
          style={{ padding: "40px 0" }}
        />
      ) : (
        <div style={{ padding: 20 }}>
          <Table
            columns={columns}
            dataSource={data}
            pagination={false}
            rowKey="id"
            size="small"
            rowClassName={(_, index) => (index % 2 === 0 ? 'striped-row' : '')}
          />
        </div>
      )}
    </Card>
  );
};

export default TopAwardeesTable;
